import {
  Button,
  Group,
  NumberInput,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { DeviceConnectionStatus } from '@types';
import { Play, Trash2 } from 'lucide-react';
import React from 'react';
import { useParams } from 'react-router-dom';
import { useBaselineCalculation } from '../../../../hooks/useBaselineCalculation';
import { useSession } from '../../../../hooks/useSession';

type BaselineProps = {
  children: React.ReactNode;
};

function Baseline({ children }: BaselineProps) {
  return <Stack gap='xs'>{children}</Stack>;
}

function BaselineInProgress() {
  const {
    baselineDefaultDuration: baselineDuration,
    baselineTimer,
    stopBaselineCalculation,
    isBaselineCalculating,
  } = useBaselineCalculation();
  const timeToRegisterBaselineLeft = baselineDuration - baselineTimer;

  if (!isBaselineCalculating) return null;

  return (
    <>
      <Button
        disabled
        size='md'
        w={'100%'}
        style={{
          position: 'relative',
          background: `linear-gradient(90deg, var(--mantine-color-blue-1) ${
            (timeToRegisterBaselineLeft * 100) / baselineDuration
          }%, #fafafa ${
            (timeToRegisterBaselineLeft * 100) / baselineDuration
          }%)`,
          color: 'var(--mantine-color-blue-7)',
          border: '1px solid var(--mantine-color-blue-1)',
          overflow: 'hidden',
        }}
      >
        <Group>
          <span>Calcolo baseline in corso...</span>
          <Text size='sm' c='dimmed'>
            {timeToRegisterBaselineLeft}
          </Text>
        </Group>
      </Button>
      <Button
        variant='outline'
        color='red'
        radius='sm'
        leftSection={<Trash2 size={16} color='red' />}
        onClick={stopBaselineCalculation}
      >
        Interrompi calcolo
      </Button>
    </>
  );
}

type IdleProps = {
  isDisabled: boolean;
  deviceStatus: DeviceConnectionStatus;
  sensorStatus: DeviceConnectionStatus;
  onStart?: () => void;
  onSkip: () => void;
  label: string;
};

function BaselineIdle({
  isDisabled,
  deviceStatus,
  sensorStatus,
  onStart,
  onSkip,
  label,
}: IdleProps) {
  const { id } = useParams();
  const { setStatus: setSessionStatus } = useSession(id);
  const {
    isBaselineCalculating,
    startBaselineCalculation,
    stopBaselineCalculation,
  } = useBaselineCalculation();

  const isSensorDisabled = sensorStatus === 'disconnected';
  const isSensorConnectedNoData = sensorStatus === 'connected_no_data';

  const isVrConnected =
    deviceStatus === 'connected' || deviceStatus === 'receiving_data';
  const isVrDisabled = deviceStatus === 'disconnected';
  const isVrConnectedNoData = deviceStatus === 'connected_no_data';

  const tooltipSensorLabel = React.useMemo(() => {
    switch (true) {
      case isSensorDisabled:
        return 'Non è possibile registrare la baseline finché il sensore HR non è connesso';
      case isSensorConnectedNoData:
        return 'Il sensore HR è connesso ma non sta inviando dati. Attendere che inizi a trasmettere.';
      case isVrDisabled:
        return 'Non è possibile registrare la baseline finché il visore VR non è connesso';
      case isVrConnectedNoData:
        return 'Il visore VR è connesso ma non sta inviando dati.';
      default:
        return null;
    }
  }, [
    isSensorDisabled,
    isSensorConnectedNoData,
    isVrDisabled,
    isVrConnectedNoData,
  ]);

  // Check if VR is still connected, if not stop baseline calculation
  React.useEffect(() => {
    if (!isVrConnected) {
      stopBaselineCalculation();
      setSessionStatus('queued');
    }
  }, [
    isBaselineCalculating,
    isVrConnected,
    stopBaselineCalculation,
    setSessionStatus,
  ]);

  if (isBaselineCalculating) return null;

  // Se i dispositivi sono disconnessi, mostra solo "Salta baseline"
  if (isDisabled) {
    return (
      <Button
        variant='light'
        radius='sm'
        loading={isBaselineCalculating}
        disabled={isBaselineCalculating}
        onClick={onSkip}
      >
        Salta calcolo baseline
      </Button>
    );
  }
  return (
    <>
      <Tooltip
        label={tooltipSensorLabel}
        position='top'
        withArrow
        disabled={!isDisabled}
      >
        <Button
          color='blue'
          radius='sm'
          size='md'
          leftSection={<Play size={16} color='white' />}
          loading={isBaselineCalculating}
          disabled={isBaselineCalculating || isDisabled}
          onClick={() => (onStart && onStart()) || startBaselineCalculation()}
        >
          {label}
        </Button>
      </Tooltip>

      {/* Pulsante "Salta calcolo baseline" sempre visibile quando i dispositivi sono online */}
      {!isBaselineCalculating && (
        <Button
          variant='light'
          radius='sm'
          loading={isBaselineCalculating}
          disabled={isBaselineCalculating}
          onClick={onSkip}
        >
          Salta calcolo baseline
        </Button>
      )}
    </>
  );
}

type SettingProps = {
  disabled?: boolean;
  value?: number;
  onChange?: (duration: number) => void;
};

function BaselineSetting({ disabled, onChange }: SettingProps) {
  const {
    baselineDefaultDuration: baselineDuration,
    setBaselineDefaultDuration: setSessionBaselineDuration,
    isBaselineCalculating,
  } = useBaselineCalculation();

  return (
    <Group align='center' gap={10} justify='space-between' mt={5}>
      <Tooltip
        label="Tempo di misurazione per stabilire la baseline cardiaca. Periodo di riferimento per calcolare le variazioni durante l'esperienza. Valore consigliato: 60 secondi."
        position='right'
        withArrow
        multiline
        w={300}
      >
        <Text c='dimmed' size='xs' style={{ cursor: 'help' }}>
          Durata della misurazione della baseline
        </Text>
      </Tooltip>
      <Group gap={5}>
        <NumberInput
          maw={100}
          disabled={disabled ?? isBaselineCalculating}
          value={baselineDuration}
          min={0}
          onChange={e => {
            const val = typeof e === 'string' ? parseInt(e) : e;
            if (onChange) onChange(val);
            else setSessionBaselineDuration(val);
          }}
        />
        <Text size='sm' c='dimmed'>
          secondi
        </Text>
      </Group>
    </Group>
  );
}

// Attach subcomponents to Baseline
Baseline.InProgress = BaselineInProgress;
Baseline.Idle = BaselineIdle;
Baseline.Setting = BaselineSetting;

export { Baseline };

// Example usage wrapper for backward compatibility
export function BaselineCalculator({
  isDisabled,
  deviceStatus,
  sensorStatus,
  onStart,
  onSkip,
  label,
}: {
  isDisabled: boolean;
  deviceStatus: DeviceConnectionStatus;
  sensorStatus: DeviceConnectionStatus;
  onStart: () => void;
  onSkip: () => void;
  label: string;
}) {
  return (
    <Baseline>
      <Baseline.InProgress />
      <Baseline.Idle
        isDisabled={isDisabled}
        deviceStatus={deviceStatus}
        sensorStatus={sensorStatus}
        onStart={onStart}
        onSkip={onSkip}
        label={label}
      />
      <Baseline.Setting />
    </Baseline>
  );
}
